import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Info, ClipboardCheck, Ruler, FileCheck, Wrench } from "lucide-react";
import { COMPANY } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const KEY_POINTS = [
    "Your property must be located in England or Wales.",
    "The grant supports replacing an eligible fossil-fuel heating system with a low-carbon alternative such as an air source heat pump.",
    "Under the scheme, voucher applications are made by an MCS-certified installer on behalf of the property owner.",
    "Eligibility, property suitability and the scheme rules in force at the time all apply — funding cannot be guaranteed.",
];

const PROCESS = [
    { icon: ClipboardCheck, title: "Property Assessment", text: "Your property, existing heating system and requirements are assessed to establish suitability." },
    { icon: Ruler, title: "System Design & Quotation", text: "Heating requirements are calculated and a system is designed and quoted for your property." },
    { icon: FileCheck, title: "Grant Application", text: "Where the scheme applies to your project, a voucher application is made by an MCS-certified installer before work begins." },
    { icon: Wrench, title: "Installation & Handover", text: "The system is installed, commissioned and handed over with a full demonstration." },
];

export default function BoilerUpgradeScheme() {
    return (
        <>
            <SEO
                title="Boiler Upgrade Scheme — up to £7,500 | Virtex Renewables"
                description="The UK Government's Boiler Upgrade Scheme offers eligible property owners in England and Wales up to £7,500 towards an air source heat pump. Subject to eligibility and scheme rules."
            />

            <section className="relative overflow-hidden bg-slate-950 py-20 md:py-28" data-testid="bus-hero">
                <p className="pointer-events-none absolute -right-8 top-1/2 hidden -translate-y-1/2 select-none font-display text-[15rem] font-extrabold leading-none text-white/5 lg:block" aria-hidden="true">
                    £7.5k
                </p>
                <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-emerald-600/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <span className="inline-block rounded-full bg-emerald-500/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                            Boiler Upgrade Scheme
                        </span>
                        <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Up to <span className="text-emerald-400">£7,500</span> Towards an Air Source Heat Pump
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                            The UK Government's Boiler Upgrade Scheme provides eligible property owners in England and
                            Wales with a grant of up to <strong className="text-white">£7,500</strong> towards the cost of
                            replacing an eligible fossil-fuel heating system with an air source heat pump.
                        </p>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">
                            Eligibility, property suitability and current scheme requirements apply.
                        </p>
                        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/contact#enquiry-form"
                                data-testid="bus-hero-check-button"
                                className="group flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-500"
                            >
                                Check Your Property
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </Link>
                            <a
                                href={COMPANY.phoneHref}
                                data-testid="bus-hero-call-button"
                                className="flex items-center justify-center gap-2 rounded-full border border-slate-700 px-7 py-4 text-sm font-bold text-white transition-colors duration-200 hover:border-emerald-500 hover:text-emerald-400"
                            >
                                <Phone className="h-4 w-4" /> Call {COMPANY.phoneDisplay}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="max-w-2xl">
                        <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                            Key Points
                        </span>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            How the Scheme Works
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-5 md:grid-cols-2">
                        {KEY_POINTS.map((point, i) => (
                            <Reveal key={point} delay={i * 0.07}>
                                <div className="flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid={`bus-point-${i}`}>
                                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                                    <p className="text-sm font-medium leading-relaxed text-slate-700">{point}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="mt-8">
                        <div className="flex items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="bus-status-note">
                            <Info className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
                            <p className="text-sm leading-relaxed text-amber-900">
                                <strong>A note on our status:</strong> Virtex Renewables' installer certification and
                                scheme arrangements are currently being established. You are very welcome to enquire
                                now — we will confirm the options available for your project, including grant funding
                                routes, when we discuss your requirements.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            How Grant Funding Fits Into Your Project
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {PROCESS.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`bus-process-${i}`}>
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
                                        <s.icon className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <p className="mt-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                                        Step {i + 1}
                                    </p>
                                    <h3 className="mt-1.5 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 text-center">
                        <p className="text-xs italic text-slate-500">
                            Boiler Upgrade Scheme funding is subject to eligibility and current scheme rules.
                        </p>
                    </Reveal>
                </div>
            </section>

            <CTABand
                title="Want to Know If Your Property Qualifies?"
                text="Tell us about your property and current heating system, and our team will talk you through suitability and the funding routes that may apply."
                primaryLabel="Check Your Property"
            />
        </>
    );
}
